/**
 * Account view (Block 2.3) — change password over POST /api/account/password.
 * The backend re-authenticates (current password) and invalidates all OTHER
 * sessions; we tell the user their other sessions were signed out.
 */

import { useState } from "react";

import { ApiError, ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";

import "./ryfe.scss";

export const RyfeAccount = () => {
  const { user, workspace } = useRyfe();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next !== confirm) {
      setError("The new passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await ryfeApi.changePassword(current, next);
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not change password",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">Mein Account</h1>

      <div className="ryfe-panel">
        <dl className="ryfe-deflist">
          <dt>E-Mail</dt>
          <dd>{user?.email}</dd>
          <dt>Workspace</dt>
          <dd>{workspace?.name}</dd>
          <dt>Rolle</dt>
          <dd>{workspace?.role}</dd>
          {/* V1.2 Block 3 — the own Freigabe-ID with a copy button. Others add
              you to shares with this id; knowing it is the introduction. */}
          <dt>Freigabe-ID</dt>
          <dd>
            <code data-testid="share-id">{user?.shareId || "—"}</code>{" "}
            {user?.shareId && (
              <button
                className="ryfe-chip"
                onClick={() =>
                  navigator.clipboard?.writeText(user.shareId || "")
                }
                title="Freigabe-ID kopieren"
              >
                Kopieren
              </button>
            )}
          </dd>
        </dl>
      </div>

      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">Passwort ändern</h2>
        <form className="ryfe-form" onSubmit={submit}>
          <label className="ryfe-field">
            <span>Aktuelles Passwort</span>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </label>
          <label className="ryfe-field">
            <span>Neues Passwort</span>
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <label className="ryfe-field">
            <span>Neues Passwort bestätigen</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
            />
          </label>

          {error && (
            <div className="ryfe-auth__error" role="alert">
              {error}
            </div>
          )}
          {done && (
            <div className="ryfe-notice" role="status">
              Passwort geändert. Alle anderen Sitzungen wurden abgemeldet.
            </div>
          )}

          <button
            className="ryfe-btn ryfe-btn--primary"
            type="submit"
            disabled={busy}
          >
            {busy ? "…" : "Passwort ändern"}
          </button>
        </form>
      </div>
    </div>
  );
};
